/*!
 * TinyPoker 營收總覽組件
 * 顯示 TinyPoker 聯盟的營收數據
 */
import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Row,
  Col,
  Table,
  Alert,
  Input,
  FormGroup,
  Label,
  Badge,
  Progress,
} from "reactstrap";
import axios from "axios";
import TinyWeekSelector from "../../components/tiny/TinyWeekSelector";
import TinyPageHeader from "components/tiny/TinyPageHeader";
import TinyLoading, { TinyLoadingOverlay } from "components/tiny/TinyLoading";

const API_BASE = "/api/tiny";

function TinyRevenue() {
  // State
  const [weeks, setWeeks] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState("");
  const [revenueData, setRevenueData] = useState(null);
  const [clubRevenues, setClubRevenues] = useState([]);
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 載入週列表
  const loadWeeks = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/data/weeks`);
      if (res.data.code === 1) {
        const allWeeks = res.data.data.weeks || [];
        setWeeks(allWeeks);
        if (allWeeks.length > 0) {
          setSelectedWeek(allWeeks[0]);
        }
      }
    } catch (err) {
      console.error("載入週列表失敗:", err);
    }
  }, []);

  // 載入營收數據
  const loadRevenueData = useCallback(async () => {
    if (!selectedWeek) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // 載入營收報表
      const revenueRes = await axios.get(`${API_BASE}/analytics/revenue?week=${selectedWeek}`);
      if (revenueRes.data.code === 1) {
        setRevenueData(revenueRes.data.data);
      }
      
      // 載入俱樂部營收
      const clubRes = await axios.get(`${API_BASE}/club-rake/detail?weeks=${selectedWeek}`);
      if (clubRes.data.code === 1) {
        setClubRevenues(clubRes.data.data.clubs || []);
      }
      
      // 載入趨勢（最近 8 週）
      const trendWeeks = weeks.slice(0, 8).join(",");
      if (trendWeeks) {
        const trendRes = await axios.get(`${API_BASE}/analytics/trend?weeks=8`);
        if (trendRes.data.code === 1) {
          setTrend(trendRes.data.data.trend || []);
        }
      }
    } catch (err) {
      setError("載入營收數據失敗: " + (err.response?.data?.msg || err.message));
    } finally {
      setLoading(false);
    }
  }, [selectedWeek, weeks]);

  // 初始載入
  useEffect(() => {
    loadWeeks();
  }, [loadWeeks]);

  // 當週期改變時載入數據
  useEffect(() => {
    if (selectedWeek) {
      loadRevenueData();
    }
  }, [selectedWeek, loadRevenueData]);

  // 格式化數字
  const formatNumber = (num) => {
    if (num === null || num === undefined) return "-";
    return parseFloat(num).toLocaleString(undefined, { maximumFractionDigits: 0 });
  };

  // 格式化金額
  const formatCurrency = (num) => {
    if (num === null || num === undefined) return "-";
    return `$${formatNumber(num)}`;
  };

  // 計算百分比
  const getPercentage = (value, total) => {
    if (!total) return 0;
    return ((value / total) * 100).toFixed(1);
  };

  return (
    <div className="content tiny-revenue-page">
      {error && (
        <Alert color="danger" toggle={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* 這頁原本把「標題」塞成選擇器卡右邊的一行小灰字（Badge TinyPoker ＋
          「營收數據總覽」），週選擇器反而是主角。改成 header 收身分、
          週選擇器進 scope —— 它確實是頁面層級的範圍控制（決定看哪一週的營收）。 */}
      <TinyPageHeader
        title="營收總覽"
        description="TinyPoker 單週的營收明細：總水、分潤、獎池與淨營收。"
        scope={
          <TinyWeekSelector
            label=""
            options={weeks}
            value={selectedWeek}
            onChange={setSelectedWeek}
            showMeta={false}
          />
        }
      />

      {loading && !revenueData ? (
        <TinyLoading text="載入週期資料" variant="page" />
      ) : (
        <TinyLoadingOverlay active={loading} text="載入週期資料">
          {/* 營收統計卡片 */}
          {revenueData && (
            <Row className="mb-3">
              <Col xl={3} md={6} sm={6} className="mb-3">
                <Card className="card-stats tiny-revenue-stat-card h-100">
                  <CardBody>
                    <Row>
                      <Col xs={5}>
                        <div className="icon-big text-center">
                          <i className="fa fa-usd text-success" />
                        </div>
                      </Col>
                      <Col xs={7}>
                        <div className="numbers">
                          <p className="card-category">總服務費</p>
                          <CardTitle tag="p">
                            {formatCurrency(revenueData.rake?.total_rake_fee)}
                          </CardTitle>
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
              <Col xl={3} md={6} sm={6} className="mb-3">
                <Card className="card-stats tiny-revenue-stat-card h-100">
                  <CardBody>
                    <Row>
                      <Col xs={5}>
                        <div className="icon-big text-center">
                          <i className="fa fa-minus-circle text-warning" />
                        </div>
                      </Col>
                      <Col xs={7}>
                        <div className="numbers">
                          <p className="card-category">MTT 服務費</p>
                          <CardTitle tag="p">
                            {formatCurrency(revenueData.rake?.mtt_rake)}
                          </CardTitle>
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
              <Col xl={3} md={6} sm={6} className="mb-3">
                <Card className="card-stats tiny-revenue-stat-card h-100">
                  <CardBody>
                    <Row>
                      <Col xs={5}>
                        <div className="icon-big text-center">
                          <i className="fa fa-check-circle text-info" />
                        </div>
                      </Col>
                      <Col xs={7}>
                        <div className="numbers">
                          <p className="card-category">淨服務費</p>
                          <CardTitle tag="p">
                            {formatCurrency(revenueData.rake?.net_rake)}
                          </CardTitle>
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
              <Col xl={3} md={6} sm={6} className="mb-3">
                <Card className="card-stats tiny-revenue-stat-card h-100">
                  <CardBody>
                    <Row>
                      <Col xs={5}>
                        <div className="icon-big text-center">
                          <i className="fa fa-pie-chart text-primary" />
                        </div>
                      </Col>
                      <Col xs={7}>
                        <div className="numbers">
                          <p className="card-category">已分配</p>
                          <CardTitle tag="p">
                            {revenueData.summary?.allocation_percentage || "0%"}
                          </CardTitle>
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}

          {/* 服務費分佈 */}
          {revenueData?.rake?.breakdown && (
            <Row className="mb-3">
              <Col md={6}>
                <Card>
                  <CardHeader>
                    <CardTitle tag="h5">
                      <i className="fa fa-pie-chart mr-2" />
                      服務費分佈
                    </CardTitle>
                  </CardHeader>
                  <CardBody>
                    {Object.entries(revenueData.rake.breakdown).map(([type, value]) => {
                      const total = revenueData.rake.total_rake_fee || 1;
                      const pct = getPercentage(value, total);
                      const colors = {
                        ring_game: "success",
                        aof: "warning",
                        sng: "info",
                        mtt: "primary"
                      };
                      return (
                        <div key={type} className="mb-3">
                          <div className="d-flex justify-content-between mb-1">
                            <span className="text-capitalize">{type.replace("_", " ")}</span>
                            <span>{formatCurrency(value)} ({pct}%)</span>
                          </div>
                          <Progress 
                            value={parseFloat(pct)} 
                            color={colors[type] || "secondary"} 
                          />
                        </div>
                      );
                    })}
                  </CardBody>
                </Card>
              </Col>
              <Col md={6}>
                <Card>
                  <CardHeader>
                    <CardTitle tag="h5">
                      <i className="fa fa-balance-scale mr-2" />
                      分配摘要
                    </CardTitle>
                  </CardHeader>
                  <CardBody>
                    {revenueData.summary && (
                      <>
                        <div className="mb-3">
                          <div className="d-flex justify-content-between mb-1">
                            <span>已分配給俱樂部</span>
                            <span>{formatCurrency(revenueData.summary.total_allocated)}</span>
                          </div>
                          <Progress 
                            value={parseFloat(revenueData.summary.allocation_percentage)} 
                            color="info" 
                          />
                        </div>
                        <div className="mb-3">
                          <div className="d-flex justify-content-between mb-1">
                            <span>聯盟剩餘</span>
                            <span>{formatCurrency(revenueData.summary.remaining)}</span>
                          </div>
                          <Progress 
                            value={parseFloat(revenueData.summary.remaining_percentage)} 
                            color="success" 
                          />
                        </div>
                        <hr />
                        <div className="text-center">
                          <h4 className="text-success">
                            聯盟淨利: {formatCurrency(revenueData.summary.remaining)}
                          </h4>
                        </div>
                      </>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}

          {/* 俱樂部營收排行 */}
          <Card className="tiny-revenue-rank-card">
            <CardHeader>
              <CardTitle tag="h5">
                <i className="fa fa-building mr-2" />
                俱樂部服務費排行
              </CardTitle>
            </CardHeader>
            <CardBody>
              {clubRevenues.length === 0 ? (
                <p className="text-muted text-center">無資料</p>
              ) : (
                <>
                  <div className="d-none d-md-block revenue-table-wrap">
                    <Table responsive hover className="revenue-table mb-0">
                      <thead className="text-primary">
                        <tr>
                          <th>#</th>
                          <th>俱樂部</th>
                          <th>類型</th>
                          <th className="text-right">Ring Game</th>
                          <th className="text-right">AOF</th>
                          <th className="text-right">MTT</th>
                          <th className="text-right">總服務費</th>
                          <th className="text-right">佔比</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clubRevenues.slice(0, 15).map((club, idx) => {
                          const totalRake = revenueData?.rake?.total_rake_fee || 1;
                          return (
                            <tr key={club.club_id + club.club_type + idx}>
                              <td>{idx + 1}</td>
                              <td className="font-weight-bold">{club.club_name}</td>
                              <td>
                                <Badge color={club.is_partner ? "warning" : club.is_self_operated ? "info" : "success"} size="sm"
                                       style={club.is_self_operated ? { background: 'linear-gradient(135deg,#00d6b4,#0d9488)' } : undefined}>
                                  {club.is_partner ? "夥伴" : club.is_self_operated ? "自營Bot" : "真人"}
                                </Badge>
                              </td>
                              <td className="text-right">{formatCurrency(club.nlh_plo_rake)}</td>
                              <td className="text-right">{formatCurrency(club.aof_rake)}</td>
                              <td className="text-right">{formatCurrency(club.mtt_rake)}</td>
                              <td className="text-right"><strong>{formatCurrency(club.total_rake)}</strong></td>
                              <td className="text-right">{getPercentage(club.total_rake, totalRake)}%</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>

                  <div className="d-md-none">
                    {clubRevenues.slice(0, 15).map((club, idx) => {
                      const totalRake = revenueData?.rake?.total_rake_fee || 1;
                      return (
                        <Card className="tiny-revenue-mobile-row" key={club.club_id + club.club_type + idx}>
                          <CardBody>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <div>
                                <div className="tiny-rank">#{idx + 1}</div>
                                <div className="tiny-club-name">{club.club_name}</div>
                              </div>
                              <Badge color={club.is_partner ? "warning" : club.is_self_operated ? "info" : "success"} size="sm"
                                     style={club.is_self_operated ? { background: 'linear-gradient(135deg,#00d6b4,#0d9488)' } : undefined}>
                                {club.is_partner ? "夥伴" : club.is_self_operated ? "自營Bot" : "真人"}
                              </Badge>
                            </div>

                            <div className="tiny-mobile-grid">
                              <div>
                                <small>Ring Game</small>
                                <div>{formatCurrency(club.nlh_plo_rake)}</div>
                              </div>
                              <div>
                                <small>AOF</small>
                                <div>{formatCurrency(club.aof_rake)}</div>
                              </div>
                              <div>
                                <small>MTT</small>
                                <div>{formatCurrency(club.mtt_rake)}</div>
                              </div>
                              <div>
                                <small>佔比</small>
                                <div>{getPercentage(club.total_rake, totalRake)}%</div>
                              </div>
                            </div>

                            <div className="tiny-total mt-2">
                              總服務費：<strong>{formatCurrency(club.total_rake)}</strong>
                            </div>
                          </CardBody>
                        </Card>
                      );
                    })}
                  </div>
                </>
              )}
            </CardBody>
          </Card>

          {/* 趨勢 */}
          {trend.length > 0 && (
            <Card className="mt-3">
              <CardHeader>
                <CardTitle tag="h5">
                  <i className="fa fa-line-chart mr-2" />
                  近期趨勢
                </CardTitle>
              </CardHeader>
              <CardBody>
                <div className="d-none d-md-block revenue-table-wrap">
                  <Table responsive size="sm" className="revenue-table mb-0">
                    <thead className="text-primary">
                      <tr>
                        <th>週期</th>
                        <th className="text-right">總服務費</th>
                        <th className="text-right">淨服務費</th>
                        <th className="text-right">聯盟淨利</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trend.map((t, idx) => (
                        <tr key={t.week || idx}>
                          <td><strong>{t.week}</strong></td>
                          <td className="text-right">{formatCurrency(t.total_rake)}</td>
                          <td className="text-right">{formatCurrency(t.net_rake)}</td>
                          <td className="text-right text-success">{formatCurrency(t.remaining)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                <div className="d-md-none">
                  {trend.map((t, idx) => (
                    <Card className="tiny-revenue-mobile-row" key={t.week || idx}>
                      <CardBody>
                        <div className="tiny-club-name mb-2">{t.week}</div>
                        <div className="tiny-mobile-grid">
                          <div>
                            <small>總服務費</small>
                            <div>{formatCurrency(t.total_rake)}</div>
                          </div>
                          <div>
                            <small>淨服務費</small>
                            <div>{formatCurrency(t.net_rake)}</div>
                          </div>
                          <div>
                            <small>聯盟淨利</small>
                            <div className="text-success">{formatCurrency(t.remaining)}</div>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  ))}
                </div>
              </CardBody>
            </Card>
          )}
        </TinyLoadingOverlay>
      )}
      <style>{`
        .tiny-revenue-page .card {
          border-radius: 12px;
        }
        .tiny-revenue-week-card .badge {
          font-size: 12px;
          padding: 6px 10px;
        }
        .tiny-revenue-stat-card .card-title {
          font-size: 1.05rem;
          margin-bottom: 0;
          word-break: break-word;
        }
        .revenue-table-wrap {
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 10px;
          overflow: hidden;
        }
        .revenue-table {
          margin-bottom: 0;
        }
        .revenue-table thead th {
          background: rgba(33, 37, 41, 0.7);
          border-top: none;
          white-space: nowrap;
        }
        .revenue-table tbody td {
          vertical-align: middle;
          white-space: nowrap;
        }
        .tiny-revenue-mobile-row {
          margin-bottom: 10px;
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 12px;
        }
        .tiny-revenue-mobile-row .card-body {
          padding: 12px;
        }
        .tiny-rank {
          font-size: 12px;
          color: #8ea4c4;
          font-weight: 700;
        }
        .tiny-club-name {
          font-size: 15px;
          font-weight: 700;
          color: #f7f9fc;
          line-height: 1.3;
        }
        .tiny-mobile-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 10px 12px;
        }
        .tiny-mobile-grid small {
          display: block;
          color: #8ea4c4;
          font-size: 11px;
        }
        .tiny-mobile-grid div > div {
          color: #fff;
          font-weight: 600;
          font-size: 14px;
          line-height: 1.2;
          margin-top: 2px;
        }
        .tiny-total {
          color: #9fb6d4;
          font-size: 13px;
        }

        @media (max-width: 1399.98px) {
          .tiny-revenue-page .icon-big i {
            font-size: 1.65rem;
          }
          .tiny-revenue-stat-card .card-category {
            font-size: 12px;
          }
        }

        @media (max-width: 991.98px) {
          .tiny-revenue-week-card .text-right {
            text-align: left !important;
            margin-top: 8px;
          }
        }

        @media (max-width: 767.98px) {
          .tiny-revenue-page .content {
            padding-left: 10px;
            padding-right: 10px;
          }
          .tiny-revenue-page .card-header h5 {
            font-size: 15px;
            margin-bottom: 0;
          }
        }
      `}</style>
    </div>
  );
}

export default TinyRevenue;


